import React, { Fragment, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { Col, Card } from "react-bootstrap";
import { Breakpoint } from "react-socks";
import PropTypes from "prop-types";

import LoadingSkeleton from "components/atoms/LoadingSkeleton/LoadingSkeleton";
import Alert from "components/molecules/Alert/Alert";
import errorToast from "helpers/errorToast";
import usePermissions from "hooks/usePermissions";
import Button from "components/atoms/Button/Button";
import testIds from "constants/testIds";
import { useGetNotifications } from "api/queries/user/useGetNotifications";
import { getWidgetRecentUpdatesTranslations } from "helpers/translationHelpers";

const RecentUpdatesWidget = function ({
  isLayoutLocked,
  isPreview,
  paymentLocation,
  widgetId,
  handleWidgetDialogOpen,
  widgetDataSource,
}) {
  const intl = useIntl();
  const navigate = useNavigate();
  const { permissions } = usePermissions();
  const paymentLocations = JSON.parse(permissions || "{}")?.payment_location?.locations;
  const paymentLocationLabel = paymentLocations?.find((pl) => pl.value === paymentLocation)?.label;

  const [error, setError] = useState(false);

  const { data: notifications, isFetching } = useGetNotifications(
    {
      onError: (err) => {
        setError(true);
        errorToast(
          err,
          intl.formatMessage({
            id: "dashboard.recentUpdatesWidget.couldNotRetrieveRecentUpdates",
            defaultMessage: "Could not retrieve recent updates. Please try again later.",
          }),
        );
      },
    },
    paymentLocation !== "All" ? paymentLocation : null,
  );

  function generateUrl(type, reference, shipment_reference) {
    let path = "";

    switch (type) {
      case "quote_request":
        path = `/shipment/${reference}`;
        break;
      case "shipment":
        path = `/shipment/${reference}/tracking`;
        break;
      case "invoice":
        return `/shipment/${shipment_reference}/billing`;
      default:
        path = `/shipment/${reference}`;
    }
    return path;
  }

  const capitalize = (s) => s && s[0].toUpperCase() + s.slice(1);

  if (isFetching) {
    return (
      <div className="overflow-hidden">
        <LoadingSkeleton
          header={intl.formatMessage({
            id: "dashboard.yourUpdates",
            defaultMessage: "Your updates",
          })}
          rows={isPreview ? 2 : 6}
          className="pl-16 pr-16 pt-1"
        />
      </div>
    );
  }

  return (
    <Card.Body className="overflow-auto">
      <div
        className={`d-flex justify-content-${
          isLayoutLocked ? "center" : "between"
        } align-items-center pb-2 br-5 mb-2 bg-white  rounded-bottom-left-0 rounded-bottom-right-0`}
      >
        <h5 className="pl-1">
          {widgetDataSource === "all"
            ? intl.formatMessage({
                id: "dashboard.widgetDialog.listWidgetBuilder.recentUpdate",
                defaultMessage: "Recent updates",
              })
            : `${capitalize(
                getWidgetRecentUpdatesTranslations(widgetDataSource.replace("_", " "), intl),
              )} ${intl.formatMessage({
                id: "tracking.updates",
                defaultMessage: "updates",
              })}`}
          {paymentLocation && paymentLocation !== "All" && (
            <span className="font-b2 text-secondary ml-2">({paymentLocationLabel})</span>
          )}
        </h5>
        {!isLayoutLocked && !isPreview && (
          <Button
            className="edit-btn"
            onClick={() => {
              handleWidgetDialogOpen(widgetId);
            }}
            data-testid={`${testIds.EDIT_WIDGET_BUTTON}-${widgetId}`}
          >
            <FormattedMessage id="general.edit" defaultMessage="Edit" />
          </Button>
        )}
      </div>
      {notifications.length > 0 && !error && (
        <>
          {!notifications.filter((update) => {
            if (widgetDataSource === "all") return update;
            if (update.type === widgetDataSource) return update;
            return null;
          }).length && (
            <div className="d-flex justify-content-center align-items-center" style={{ height: "80%" }}>
              <span className="text-secondary text-center">
                <FormattedMessage id="general.noUpdates" defaultMessage="No updates" />{" "}
                {paymentLocationLabel && <>for {paymentLocationLabel}</>}
              </span>
            </div>
          )}
          <ul className="row flex-column list-widget-container">
            {notifications
              .filter((update) => {
                if (widgetDataSource === "all") return update;
                if (update.type === widgetDataSource) return update;
                return null;
              })
              .slice(0, isPreview ? 2 : 10)
              .map(({ notification, reference, reference_title, shipment_reference, type, id }, j) => {
                const firstTruncate = reference_title.substr(0, 35);
                const secondTruncate = firstTruncate.substr(
                  0,
                  Math.min(firstTruncate.length, firstTruncate.lastIndexOf(" ")),
                );
                return (
                  <Fragment key={id}>
                    <li
                      className={`col list-widget-item ${!isLayoutLocked || isPreview ? "previewing" : ""} ${
                        j === notifications.length - 1 ? "" : "border-bottom border-grey"
                      }`}
                    >
                      <div
                        aria-hidden
                        onClick={() => {
                          if (isLayoutLocked && !isPreview) {
                            navigate(generateUrl(type, reference, shipment_reference));
                          }
                        }}
                        className="d-flex row text-primary text-decoration-none pt-4 pb-4"
                      >
                        <Col className="d-flex flex-column align-items-start justify-content-start">
                          <span className="text-primary font-b4 d-block pb-1">
                            {reference} {reference_title ? "|" : ""}{" "}
                            {reference_title.length > 35
                              ? `${secondTruncate === "" ? firstTruncate : secondTruncate}...`
                              : reference_title}
                          </span>
                          <p className="font-b3">{notification}</p>
                        </Col>
                        {widgetDataSource === "all" && (
                          <Breakpoint className="col-lg-4 text-right d-flex flex-column align-items-end" large up>
                            <span className="font-b5 text-secondary text-capitalize">{type.replace("_", " ")}</span>
                          </Breakpoint>
                        )}
                      </div>
                    </li>
                    <hr className="list-widget-line" />
                  </Fragment>
                );
              })}
          </ul>
        </>
      )}
      {!notifications.length && !error && (
        <div className="d-flex justify-content-center align-items-center" style={{ height: "80%" }}>
          <span className="text-secondary text-center">
            No updates {paymentLocationLabel && <>for {paymentLocationLabel}</>}
          </span>
        </div>
      )}
      {error && (
        <Alert className="mb-0" variant="danger">
          <FormattedMessage
            id="dashboard.couldNotRetrieveUpdates"
            defaultMessage="We could not retrieve your updates. Please try again."
          />
        </Alert>
      )}
    </Card.Body>
  );
};

RecentUpdatesWidget.propTypes = {
  paymentLocation: PropTypes.string,
  isLayoutLocked: PropTypes.bool.isRequired,
  isPreview: PropTypes.bool,
  widgetId: PropTypes.number,
  handleWidgetDialogOpen: PropTypes.func,
  widgetDataSource: PropTypes.string,
};

RecentUpdatesWidget.defaultProps = {
  paymentLocation: null,
  isPreview: false,
  widgetId: null,
  handleWidgetDialogOpen: () => {},
  widgetDataSource: "all",
};

export default RecentUpdatesWidget;
